import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'next-i18next';

import type { QuizSetDetailsRes } from '@/api/gen/_model/quizSetDetailsRes';
import { useGetMyClassGroups } from '@/api/gen/classroom/classroom.gen';
import {
  showClassroomLinkModal,
  showClassroomPromotionModal,
} from '@/shared/features/classroom/components';
import { trackClassroomEvent } from '@/shared/features/classroom/utils';
import { selectData } from '@/shared/utils';

interface UseLinkToClassroomHandlerParams {
  quizData: QuizSetDetailsRes;
}

export const useLinkToClassroomHandler = ({
  quizData,
}: UseLinkToClassroomHandlerParams) => {
  const { t } = useTranslation(['common']);
  const { refetch } = useGetMyClassGroups(
    {
      sortBy: 'updatedAt',
      order: 'desc',
      linkedContentId: quizData.id,
      linkedContentType: 'QUIZSET',
    },
    {
      query: {
        // 클래스 연결 CTA는 소유자에게만 노출되므로 페이지 진입 시 선조회하지 않고
        // 버튼 클릭 시 refetch 로만 조회해 비로그인 상세 진입의 401 노이즈를 막는다.
        enabled: false,
        select: selectData,
      },
    },
  );

  return useCallback(async () => {
    trackClassroomEvent('click_link_to_classroom', {
      source: 'quiz_detail',
      contentId: quizData.id,
      contentType: 'QUIZSET',
    });

    const result = await refetch();

    if (result.isError) {
      toast.error(t('common:common.error.notLoadContent'));
      return;
    }

    const classrooms = result.data ?? [];

    if (classrooms.length === 0) {
      showClassroomPromotionModal();
      return;
    }

    showClassroomLinkModal({
      contentId: quizData.id,
      contentType: 'QUIZSET',
    });
  }, [quizData.id, refetch, t]);
};
